import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface ChecklistItem {
  task: string;
  note?: string;
}

interface Section {
  key: 'section1_done' | 'section2_done' | 'section3_done' | 'section4_done' | 'section5_done';
  title: string;
  items: ChecklistItem[];
}

const SECTIONS: Section[] = [
  {
    key: 'section1_done',
    title: 'Section 1 — Common Areas & General Property',
    items: [
      { task: 'Remove all trash from every room and outdoor area', note: 'Use trash bags provided. Tie and place at designated spot.' },
      { task: 'Replace all trash bags with fresh liners', note: 'Fresh bags must be in every bin.' },
      { task: 'Sweep all hard floors (indoor and outdoor)', note: 'No debris, dirt, or food particles remaining.' },
      { task: 'Mop all hard floors', note: 'No sticky spots, stains, or visible residue.' },
      { task: 'Wipe down all tables and flat surfaces', note: 'Clean cloth, no streaks or spills left.' },
      { task: 'Wipe down all chairs, benches, and seating', note: 'Both seat and back surfaces.' },
      { task: 'Pick up all trash, cups, bottles, and items from outdoor areas', note: 'Yard, driveway, walkways — all clear.' },
      { task: 'Remove all decorations, signage, and personal items', note: 'Nothing left behind. Owner not responsible for left items.' },
      { task: 'Return all furniture to its original position', note: 'Where it was when you arrived.' },
      { task: 'Check all areas for forgotten items (inside and outside)', note: 'Final walkthrough required.' },
    ],
  },
  {
    key: 'section2_done',
    title: 'Section 2 — Kitchen (if used)',
    items: [
      { task: 'Wipe down all countertops and surfaces', note: 'No crumbs, spills, or grease.' },
      { task: 'Clean the inside of the microwave', note: 'No food splatter on walls or turntable.' },
      { task: 'Clean the stovetop and knobs', note: 'No grease or food residue.' },
      { task: 'Wipe down the exterior of the oven and refrigerator', note: 'Handles and doors included.' },
      { task: 'Wash all dishes, cups, and utensils used', note: 'Or load dishwasher and run it.' },
      { task: 'Wipe down sink and faucet', note: 'No food particles. Leave dry.' },
      { task: 'Take out kitchen trash and replace liner' },
    ],
  },
  {
    key: 'section3_done',
    title: 'Section 3 — Bathrooms (if used)',
    items: [
      { task: 'Scrub toilet bowl, seat, and exterior', note: 'Use cleaner and brush provided.' },
      { task: 'Wipe down sink, counter, and faucet', note: 'No soap residue or water spots.' },
      { task: 'Wipe down mirror', note: 'Streak-free.' },
      { task: 'Mop or wipe bathroom floor', note: 'No water, debris, or stains.' },
      { task: 'Restock toilet paper if used', note: 'At least one roll on holder.' },
      { task: 'Empty bathroom trash and replace liner' },
      { task: 'Check and clean any splatter on walls' },
    ],
  },
  {
    key: 'section4_done',
    title: 'Section 4 — Outdoor & Yard Areas',
    items: [
      { task: 'Pick up all trash, cups, cans, and waste from yard', note: 'Every inch of the yard.' },
      { task: 'Pick up any trash on the driveway and sidewalk in front of property', note: 'Including street gutter area.' },
      { task: 'Return any outdoor furniture to original position', note: 'Tables, chairs, speakers, etc.' },
      { task: 'Check for any damage to fencing, plants, or outdoor structures', note: 'Report to Owner immediately.' },
      { task: 'Turn off all outdoor lights (unless Owner specified otherwise)' },
    ],
  },
  {
    key: 'section5_done',
    title: 'Section 5 — Final Walkthrough',
    items: [
      { task: 'Walk through every room and outdoor area one final time', note: "With Owner or Owner's representative if possible." },
      { task: 'All windows and doors are locked and secured' },
      { task: 'All lights turned off (unless Owner specified otherwise)' },
      { task: 'All personal items, equipment, and rental gear removed', note: 'No items left on property.' },
      { task: 'Any damage found has been disclosed to Owner', note: 'Undisclosed damage found later = additional charge.' },
    ],
  },
];

type CheckState = Record<string, boolean>;

function buildInitialChecks(): CheckState {
  const state: CheckState = {};
  SECTIONS.forEach(s => {
    s.items.forEach((_, i) => { state[`${s.key}_${i}`] = false; });
  });
  return state;
}

export function Checklist() {
  const { token } = useParams<{ token: string }>();
  const [pageStatus, setPageStatus] = useState<'loading' | 'ready' | 'submitted' | 'already_submitted' | 'invalid'>('loading');
  const [checks, setChecks] = useState<CheckState>(buildInitialChecks());
  const [completedBy, setCompletedBy] = useState('');
  const [incompleteSections, setIncompleteSections] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setPageStatus('invalid'); return; }
    supabase
      .from('cleaning_checklist')
      .select('*')
      .eq('token', token)
      .single()
      .then(({ data, error }) => {
        if (error || !data) { setPageStatus('invalid'); return; }
        setPageStatus(data.status === 'submitted' ? 'already_submitted' : 'ready');
      });
  }, [token]);

  const isSectionComplete = (section: Section) =>
    section.items.every((_, i) => checks[`${section.key}_${i}`]);

  const allComplete = SECTIONS.every(isSectionComplete);
  const completedCount = SECTIONS.filter(isSectionComplete).length;

  const toggle = (key: string) => setChecks(prev => ({ ...prev, [key]: !prev[key] }));

  const handleSubmit = async () => {
    setError('');
    if (!completedBy.trim()) { setError('Please enter your name before submitting.'); return; }
    const incomplete = SECTIONS.filter(s => !isSectionComplete(s)).map(s => s.title);
    if (incomplete.length > 0) {
      setIncompleteSections(incomplete);
      setError('All items must be completed. Items will be verified — if they weren\'t done, you will be charged regardless. Prefer to skip? Pay the $250 cleaning fee directly instead.');
      return;
    }
    setSubmitting(true);
    const { error: updateError } = await supabase
      .from('cleaning_checklist')
      .update({
        status: 'submitted',
        submitted_at: new Date().toISOString(),
        completed_by: completedBy.trim(),
        section1_done: true, section2_done: true, section3_done: true,
        section4_done: true, section5_done: true,
        all_complete: true,
        cleaning_fee_triggered: false,
      })
      .eq('token', token)
      .eq('status', 'pending');

    if (updateError) {
      setError('There was a problem submitting. Please try again.');
      setSubmitting(false);
    } else {
      setPageStatus('submitted');
    }
  };

  // ── Status screens ──────────────────────────────────────────────────────
  if (pageStatus === 'loading') return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (pageStatus === 'invalid') return (
    <div className="min-h-screen flex items-center justify-center px-4 text-center">
      <div>
        <h1 className="font-serif text-2xl font-bold text-gray-900 mb-2">Invalid Link</h1>
        <p className="text-gray-500">This checklist link is invalid or has expired. Contact Nuno Productions LLC.</p>
      </div>
    </div>
  );

  if (pageStatus === 'already_submitted') return (
    <div className="min-h-screen flex items-center justify-center px-4 text-center">
      <div>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="font-serif text-2xl font-bold text-gray-900 mb-2">Already Submitted</h1>
        <p className="text-gray-500">This checklist has already been submitted. You're all done!</p>
      </div>
    </div>
  );

  if (pageStatus === 'submitted') return (
    <div className="min-h-screen flex items-center justify-center px-4 text-center">
      <div>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="font-serif text-2xl font-bold text-gray-900 mb-3">Checklist Submitted!</h1>
        <p className="text-gray-600">You're all done. Thanks for taking care of the space.</p>
        <p className="text-sm text-gray-400 mt-4">— Nuno Productions LLC</p>
      </div>
    </div>
  );

  // ── Checklist page ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gray-900 text-white py-5 px-4 sticky top-0 z-10">
        <h1 className="font-serif text-xl font-bold text-gold-400 text-center">Nuno Productions LLC</h1>
        <p className="text-center text-xs text-gray-400 mt-0.5">Post-Event Cleaning Checklist</p>
        <div className="mt-2 bg-red-600 text-white text-xs font-bold text-center py-2 px-3 rounded-lg leading-snug">
          ⚠️ All items must be completed before 11:59 PM the day after your event.<br />
          <span className="font-normal">Incomplete checklist = automatic $250 charge. Items will be verified — if they weren't completed, you will be charged regardless. Prefer to skip the checklist? You can pay the $250 cleaning fee directly instead.</span>
        </div>
        {/* Progress bar */}
        <div className="mt-3 flex items-center gap-2">
          <div className="flex-1 bg-gray-700 rounded-full h-2">
            <div
              className="bg-gold-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(completedCount / SECTIONS.length) * 100}%` }}
            />
          </div>
          <span className="text-xs text-gray-300 flex-shrink-0 w-16 text-right">
            {completedCount}/{SECTIONS.length} sections
          </span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">

        {SECTIONS.map(section => {
          const sectionDone = isSectionComplete(section);
          const isIncomplete = incompleteSections.includes(section.title);
          const checkedCount = section.items.filter((_, i) => checks[`${section.key}_${i}`]).length;

          return (
            <div key={section.key}
              className={`bg-white rounded-xl border-2 overflow-hidden transition-colors ${
                isIncomplete ? 'border-red-400' : sectionDone ? 'border-green-400' : 'border-gray-200'
              }`}>
              <div className={`px-4 py-3 border-b flex items-center justify-between ${
                isIncomplete ? 'bg-red-50 border-red-200' : sectionDone ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-100'
              }`}>
                <div>
                  <h2 className={`text-sm font-bold ${sectionDone ? 'text-green-700' : isIncomplete ? 'text-red-700' : 'text-gray-800'}`}>
                    {section.title}
                  </h2>
                  <p className="text-xs text-gray-400 mt-0.5">{checkedCount}/{section.items.length} items</p>
                </div>
                {sectionDone && (
                  <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded-full flex-shrink-0">✓ Complete</span>
                )}
                {isIncomplete && !sectionDone && (
                  <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-1 rounded-full flex-shrink-0">Incomplete</span>
                )}
              </div>

              <div className="divide-y divide-gray-50">
                {section.items.map((item, i) => {
                  const checkKey = `${section.key}_${i}`;
                  const isChecked = checks[checkKey];
                  return (
                    <label key={checkKey} className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${isChecked ? 'bg-green-50/40' : 'hover:bg-gray-50'}`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggle(checkKey)}
                        className="mt-0.5 w-5 h-5 accent-gold-500 flex-shrink-0 cursor-pointer"
                      />
                      <div className="min-w-0">
                        <span className={`text-sm leading-snug block ${isChecked ? 'text-gray-400 line-through' : 'text-gray-800 font-medium'}`}>
                          {item.task}
                        </span>
                        {item.note && (
                          <span className={`text-xs mt-0.5 block ${isChecked ? 'text-gray-300' : 'text-gray-400'}`}>
                            {item.note}
                          </span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Submit section */}
        <div className="bg-white rounded-xl border-2 border-gray-200 p-5">
          <h2 className="font-serif text-lg font-bold text-gray-900 mb-1">Client Sign-Off</h2>
          <p className="text-xs text-gray-500 mb-4">
            By submitting, you confirm all items have been completed to the standard described above.
          </p>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
            <input
              type="text"
              value={completedBy}
              onChange={e => setCompletedBy(e.target.value)}
              placeholder="Full name of person completing checklist"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
            />
          </div>

          {!allComplete && (
            <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-xs text-yellow-800">
              <strong>Not complete yet.</strong> All {SECTIONS.reduce((s, sec) => s + sec.items.length, 0)} items must be checked before you can submit. Incomplete checklist = automatic $250 charge. Items will be verified. Prefer to skip? Pay the $250 cleaning fee directly instead.
            </div>
          )}

          {incompleteSections.length > 0 && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-xs text-red-700">
              <strong>These sections need attention:</strong>
              <ul className="mt-1 ml-3 list-disc space-y-0.5">
                {incompleteSections.map(s => <li key={s}>{s}</li>)}
              </ul>
            </div>
          )}

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>
          )}

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`w-full py-4 font-bold rounded-xl transition-colors text-base ${
              allComplete ? 'bg-gold-500 hover:bg-gold-600 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {submitting ? 'Submitting…'
              : allComplete ? 'Submit Checklist'
              : `${completedCount}/${SECTIONS.length} Sections Done — Keep Going`}
          </button>
        </div>
      </div>
    </div>
  );
}
